'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import { Trash2 } from 'lucide-react';
import type { Table } from '@/lib/table-features';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { STORAGE_LOCATIONS_HASH } from '../constants';
import { useDeleteStorageLocation } from '../services/storage-locations.mutations';
import type { StorageLocation } from '../types';

interface StorageLocationsTableActionBarProps {
  table: Table<StorageLocation>;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function StorageLocationsTableActionBar({
  table,
  onComplete,
  isAllPagesSelected,
  totalCount
}: StorageLocationsTableActionBarProps) {
  const t = useTranslations('storageLocations');
  const tc = useTranslations('common');
  const { open: bulkOpen, onOpenChange: toggleBulk } = useHashDialog(
    STORAGE_LOCATIONS_HASH.bulkDelete
  );

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  const deleteStorageLocation = useDeleteStorageLocation();
  const isDeleting = deleteStorageLocation.isPending;

  const handleBulkDelete = async () => {
    const ids = selectedRows.map((row) => row.original.id);

    try {
      for (const id of ids) {
        await deleteStorageLocation.mutateAsync({ id });
      }
      toggleBulk(false);
      table.resetRowSelection();
      onComplete();
    } catch {
      // Error handling is done by the mutation
    }
  };

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {displayCount} selected
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleBulk(true)}
            disabled={isDeleting}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('delete')}
          </Button>
        </div>
      </div>

      <AlertDialog open={bulkOpen} onOpenChange={toggleBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-3'>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isDeleting ? 'Deleting...' : t('delete')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
